import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSEO } from "../lib/useSEO";
import { Sparkles, LogOut, Edit, Trash2, Search, Plus } from "lucide-react";
import AdminNav from "../components/AdminNav";

export default function AdminDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | published | draft

  useSEO({ title: "Admin Dashboard | WaterfallFinder" });

  const load = async () => {
    setLoading(true);
    const r = await api.get("/admin/waterfalls", { params: { q: q || undefined, limit: 500 } });
    setItems(r.data.items);
    setTotal(r.data.total);
    setLoading(false);
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line
  }, [user]);

  if (!authLoading && !user) return <Navigate to="/admin/login" replace />;

  const onGenerate = async (id) => {
    setBusy((b) => ({ ...b, [id]: "gen" }));
    try {
      await api.post(`/admin/waterfalls/${id}/generate`);
      await load();
    } catch (e) {
      alert("AI generation failed. " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy((b) => ({ ...b, [id]: null }));
    }
  };

  const onDelete = async (id) => {
    if (!confirm("Delete this waterfall?")) return;
    setBusy((b) => ({ ...b, [id]: "del" }));
    try {
      await api.delete(`/admin/waterfalls/${id}`);
      await load();
    } finally {
      setBusy((b) => ({ ...b, [id]: null }));
    }
  };

  const onToggleDraft = async (w) => {
    setBusy((b) => ({ ...b, [w.id]: "draft" }));
    try {
      await api.put(`/admin/waterfalls/${w.id}`, { is_draft: !w.is_draft });
      await load();
    } finally {
      setBusy((b) => ({ ...b, [w.id]: null }));
    }
  };

  const visible = items.filter((w) =>
    filter === "all" ? true : filter === "draft" ? w.is_draft : !w.is_draft
  );

  return (
    <main className="min-h-screen bg-stone-50">
      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold text-stone-900">Waterfalls</h1>
            <p className="text-sm text-stone-600 mt-1">{total} entries · click any to edit, or generate AI content.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex rounded-full border border-stone-300 p-1 text-xs">
              {["all", "published", "draft"].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  data-testid={`filter-${f}`}
                  className={`px-3 py-1 rounded-full capitalize font-semibold ${filter === f ? "bg-stone-900 text-white" : "text-stone-700"}`}
                >
                  {f} {f === "all" ? `(${items.length})` : f === "draft" ? `(${items.filter(w=>w.is_draft).length})` : `(${items.filter(w=>!w.is_draft).length})`}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Search…"
                data-testid="admin-search-input"
                className="rounded-full border border-stone-300 pl-9 pr-4 py-2 text-sm w-64 outline-none focus:border-[hsl(var(--primary))]"
              />
            </div>
            <Link
              to="/admin/waterfalls/new"
              data-testid="admin-add-button"
              className="btn-pill bg-[hsl(var(--primary))] text-white text-sm py-2"
            >
              <Plus className="w-4 h-4 mr-1" /> Add new
            </Link>
          </div>
        </div>

        {loading ? (
          <p className="text-stone-500">Loading…</p>
        ) : (
          <div className="rounded-2xl bg-white border border-stone-200 overflow-hidden">
            <table className="w-full text-sm" data-testid="admin-waterfalls-table">
              <thead className="bg-stone-50 text-stone-600 text-left">
                <tr>
                  <th className="py-3 px-4">Name</th>
                  <th className="py-3 px-4 hidden md:table-cell">Country / State</th>
                  <th className="py-3 px-4 hidden lg:table-cell">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((w) => (
                  <tr key={w.id} className={`border-t border-stone-100 hover:bg-stone-50 ${w.is_draft ? "bg-amber-50/40" : ""}`}>
                    <td className="py-3 px-4">
                      <Link to={`/waterfalls/${w.slug}`} className="font-medium text-stone-900 hover:text-[hsl(var(--primary))]">
                        {w.name}
                      </Link>
                      <div className="text-xs text-stone-500">/{w.slug}</div>
                    </td>
                    <td className="py-3 px-4 hidden md:table-cell text-stone-700">
                      {w.country}{w.state ? ` · ${w.state}` : ""}
                    </td>
                    <td className="py-3 px-4 hidden lg:table-cell">
                      <button
                        onClick={() => onToggleDraft(w)}
                        disabled={busy[w.id] === "draft"}
                        data-testid={`admin-toggle-draft-${w.slug}`}
                        className={`text-xs px-2 py-1 rounded-full transition-colors disabled:opacity-50 ${
                          w.is_draft
                            ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                            : "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                        }`}
                        title={w.is_draft ? "Click to publish" : "Click to unpublish (make draft)"}
                      >
                        {w.is_draft ? "Draft" : "Published"}
                      </button>
                      {w.ai_generated && (
                        <span className="ml-2 text-xs px-2 py-1 bg-stone-100 text-stone-600 rounded-full">AI</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => onGenerate(w.id)}
                          disabled={busy[w.id] === "gen"}
                          data-testid={`admin-generate-${w.slug}`}
                          className="text-xs flex items-center gap-1 text-[hsl(var(--primary))] hover:text-[hsl(var(--accent))] disabled:opacity-50"
                          title="Re-generate AI content"
                        >
                          <Sparkles className="w-4 h-4" />
                          {busy[w.id] === "gen" ? "Generating…" : "AI"}
                        </button>
                        <Link
                          to={`/admin/waterfalls/${w.id}`}
                          data-testid={`admin-edit-${w.slug}`}
                          className="text-xs flex items-center gap-1 text-stone-600 hover:text-[hsl(var(--primary))]"
                        >
                          <Edit className="w-4 h-4" /> Edit
                        </Link>
                        <button
                          onClick={() => onDelete(w.id)}
                          disabled={busy[w.id] === "del"}
                          data-testid={`admin-delete-${w.slug}`}
                          className="text-xs flex items-center gap-1 text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
